import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import NavigationHeader from '../homepage/components/NavigationHeader';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import MetricCard from './components/MetricCard';
import VisitorFlowHeatmap from './components/VisitorFlowHeatmap';
import DemographicChart from './components/DemographicChart';
import ConversionTracker from './components/ConversionTracker';
import EnvironmentalImpactChart from './components/EnvironmentalImpactChart';
import PredictiveAnalytics from './components/PredictiveAnalytics';
import SeasonalTrends from './components/SeasonalTrends';

const VisitorAnalyticsDashboard = () => {
  const [timeRange, setTimeRange] = useState('month');
  const [selectedZone, setSelectedZone] = useState('all');

  const timeRangeOptions = [
    { value: 'week', label: 'Last 7 Days' },
    { value: 'month', label: 'Last 30 Days' },
    { value: 'quarter', label: 'Last Quarter' },
    { value: 'year', label: 'Last Year' }
  ];

  const zoneOptions = [
    { value: 'all', label: 'All Zones' },
    { value: 'core', label: 'Core Conservation Zone' },
    { value: 'buffer', label: 'Buffer Zone' },
    { value: 'transition', label: 'Transition Zone' },
    { value: 'recreation', label: 'Recreation Area' }
  ];

  const metricsData = [
    {
      title: "Total Visitors",
      value: "12,847",
      change: "+18.2%",
      changeType: "positive",
      icon: "Users",
      iconColor: "bg-primary/20 text-primary",
      trend: [12, 18, 15, 22, 28, 24, 32, 28, 35, 30, 38, 42]
    },
    {
      title: "Permit Bookings",
      value: "3,456",
      change: "+24.5%",
      changeType: "positive",
      icon: "FileText",
      iconColor: "bg-success/20 text-success",
      trend: [8, 12, 10, 15, 18, 16, 22, 20, 25, 23, 28, 30]
    },
    {
      title: "Research Subscriptions",
      value: "892",
      change: "+12.8%",
      changeType: "positive",
      icon: "BookOpen",
      iconColor: "bg-info/20 text-info",
      trend: [5, 8, 7, 10, 12, 11, 14, 13, 16, 15, 18, 20]
    },
    {
      title: "Volunteer Enrollments",
      value: "567",
      change: "+31.4%",
      changeType: "positive",
      icon: "Heart",
      iconColor: "bg-secondary/20 text-secondary",
      trend: [4, 6, 5, 8, 10, 9, 12, 11, 14, 13, 16, 18]
    },
    {
      title: "Conservation Pledges",
      value: "8,234",
      change: "+45.7%",
      changeType: "positive",
      icon: "Award",
      iconColor: "bg-accent/20 text-accent",
      trend: [10, 15, 13, 18, 22, 20, 26, 24, 30, 28, 34, 38]
    },
    {
      title: "Avg Visit Duration",
      value: "4.2 hrs",
      change: "-8.3%",
      changeType: "negative",
      icon: "Clock",
      iconColor: "bg-warning/20 text-warning",
      trend: [25, 24, 26, 23, 22, 24, 21, 23, 20, 22, 19, 21]
    }
  ];

  const heatmapData = [
    {
      id: 1,
      name: "Subterranean River Trail",
      visitors: 4523,
      intensity: 89,
      avgDuration: "2.5 hrs",
      peakTime: "10:00 AM - 2:00 PM"
    },
    {
      id: 2,
      name: "Monkey Trail",
      visitors: 3214,
      intensity: 67,
      avgDuration: "1.8 hrs",
      peakTime: "8:00 AM - 11:00 AM"
    },
    {
      id: 3,
      name: "Mangrove Boardwalk",
      visitors: 2876,
      intensity: 58,
      avgDuration: "1.2 hrs",
      peakTime: "3:00 PM - 5:00 PM"
    },
    {
      id: 4,
      name: "Jungle Trail",
      visitors: 1987,
      intensity: 42,
      avgDuration: "3.5 hrs",
      peakTime: "7:00 AM - 10:00 AM"
    },
    {
      id: 5,
      name: "Beach Access Path",
      visitors: 1543,
      intensity: 31,
      avgDuration: "0.8 hrs",
      peakTime: "4:00 PM - 6:00 PM"
    },
    {
      id: 6,
      name: "Viewpoint Trail",
      visitors: 1234,
      intensity: 25,
      avgDuration: "1.5 hrs",
      peakTime: "5:00 AM - 7:00 AM"
    }
  ];

  const originData = [
    { name: "Philippines", visitors: 4523, percentage: 35.2 },
    { name: "United States", visitors: 2876, percentage: 22.4 },
    { name: "China", visitors: 1987, percentage: 15.5 },
    { name: "Japan", visitors: 1543, percentage: 12.0 },
    { name: "South Korea", visitors: 1234, percentage: 9.6 },
    { name: "Australia", visitors: 684, percentage: 5.3 }
  ];

  const ageData = [
    { name: "18-24", visitors: 2345, percentage: 18.2 },
    { name: "25-34", visitors: 4123, percentage: 32.1 },
    { name: "35-44", visitors: 3214, percentage: 25.0 },
    { name: "45-54", visitors: 1987, percentage: 15.5 },
    { name: "55-64", visitors: 876, percentage: 6.8 },
    { name: "65+", visitors: 302, percentage: 2.4 }
  ];

  const durationData = [
    { name: "< 2 hours", visitors: 1876, percentage: 14.6 },
    { name: "2-4 hours", visitors: 4523, percentage: 35.2 },
    { name: "4-6 hours", visitors: 3987, percentage: 31.0 },
    { name: "6-8 hours", visitors: 1654, percentage: 12.9 },
    { name: "> 8 hours", visitors: 807, percentage: 6.3 }
  ];

  const conversionData = [
    {
      category: "Primary Conversions",
      total: 4915,
      conversionRate: 38.3,
      metrics: [
        { name: "Permit Bookings", count: 3456, change: "+24.5%", trend: "up" },
        { name: "Research Data Subscriptions", count: 892, change: "+12.8%", trend: "up" },
        { name: "Volunteer Program Enrollment", count: 567, change: "+31.4%", trend: "up" }
      ]
    },
    {
      category: "Micro-Conversions",
      total: 15234,
      conversionRate: 118.6,
      metrics: [
        { name: "Newsletter Signups", count: 6543, change: "+42.1%", trend: "up" },
        { name: "Offline Map Downloads", count: 4876, change: "+28.7%", trend: "up" },
        { name: "Social Media Follows", count: 2987, change: "+15.3%", trend: "up" },
        { name: "Conservation Pledge Commitments", count: 828, change: "+8.9%", trend: "up" }
      ]
    },
    {
      category: "Soft Engagements",
      total: 28456,
      conversionRate: 221.5,
      metrics: [
        { name: "Interactive Map Exploration", count: 12876, change: "+35.6%", trend: "up" },
        { name: "Species Identification Games", count: 8234, change: "+22.4%", trend: "up" },
        { name: "Virtual Habitat Tours", count: 5987, change: "+18.9%", trend: "up" },
        { name: "360° Photo Views", count: 1359, change: "-5.2%", trend: "down" }
      ]
    },
    {
      category: "Relationship Building",
      total: 9876,
      conversionRate: 76.9,
      metrics: [
        { name: "Conservation Impact Updates", count: 4523, change: "+28.3%", trend: "up" },
        { name: "Researcher Webinar Attendance", count: 2987, change: "+15.7%", trend: "up" },
        { name: "Community Project Participation", count: 1876, change: "+42.8%", trend: "up" },
        { name: "Donation Contributions", count: 490, change: "+12.1%", trend: "up" }
      ]
    }
  ];

  const environmentalData = [
    { month: "Jan", visitors: 8234, ecosystemHealth: 92, biodiversityIndex: 88 },
    { month: "Feb", visitors: 9876, ecosystemHealth: 90, biodiversityIndex: 87 },
    { month: "Mar", visitors: 12456, ecosystemHealth: 87, biodiversityIndex: 85 },
    { month: "Apr", visitors: 15234, ecosystemHealth: 84, biodiversityIndex: 83 },
    { month: "May", visitors: 11987, ecosystemHealth: 86, biodiversityIndex: 84 },
    { month: "Jun", visitors: 10234, ecosystemHealth: 88, biodiversityIndex: 86 },
    { month: "Jul", visitors: 13456, ecosystemHealth: 85, biodiversityIndex: 84 },
    { month: "Aug", visitors: 14876, ecosystemHealth: 83, biodiversityIndex: 82 },
    { month: "Sep", visitors: 11234, ecosystemHealth: 87, biodiversityIndex: 85 },
    { month: "Oct", visitors: 9876, ecosystemHealth: 89, biodiversityIndex: 87 },
    { month: "Nov", visitors: 10987, ecosystemHealth: 88, biodiversityIndex: 86 },
    { month: "Dec", visitors: 12847, ecosystemHealth: 86, biodiversityIndex: 85 }
  ];

  const predictiveData = [
    { period: "Jan 2025", predicted: 13500, actual: 12847 },
    { period: "Feb 2025", predicted: 14200, actual: null },
    { period: "Mar 2025", predicted: 16800, actual: null },
    { period: "Apr 2025", predicted: 18500, actual: null },
    { period: "May 2025", predicted: 15200, actual: null },
    { period: "Jun 2025", predicted: 13800, actual: null },
    { period: "Jul 2025", predicted: 17200, actual: null },
    { period: "Aug 2025", predicted: 19500, actual: null },
    { period: "Sep 2025", predicted: 14800, actual: null },
    { period: "Oct 2025", predicted: 12900, actual: null },
    { period: "Nov 2025", predicted: 14500, actual: null },
    { period: "Dec 2025", predicted: 16200, actual: null }
  ];

  const recommendations = [
    {
      title: "Increase Ranger Staff",
      description: "Peak season (Mar-Apr) requires 40% more rangers for Subterranean River Trail to maintain safety standards",
      priority: "high",
      icon: "Users",
      timeframe: "Implement by Feb 15"
    },
    {
      title: "Trail Maintenance Schedule",
      description: "Monkey Trail showing 67% congestion - schedule maintenance during low-traffic hours (2-4 PM)",
      priority: "medium",
      icon: "Wrench",
      timeframe: "Next 2 weeks"
    },
    {
      title: "Expand Parking Capacity",
      description: "Visitor projections indicate 25% parking shortage during April peak - consider temporary expansion",
      priority: "high",
      icon: "Car",
      timeframe: "Complete by Mar 1"
    },
    {
      title: "Educational Program Scaling",
      description: "Volunteer enrollment up 31% - expand orientation sessions to accommodate growing interest",
      priority: "medium",
      icon: "BookOpen",
      timeframe: "Ongoing"
    }
  ];

  const seasonalData = [
    {
      id: 1,
      name: "Dry Season",
      period: "November - May",
      avgVisitors: 456,
      satisfaction: 94,
      weather: "Sunny, 28-32°C",
      visibility: "Excellent",
      activities: ["Bird watching", "River tours", "Hiking", "Photography"],
      recommendation: "Optimal visiting period with highest wildlife activity and best weather conditions"
    },
    {
      id: 2,
      name: "Wet Season",
      period: "June - October",
      avgVisitors: 287,
      satisfaction: 78,
      weather: "Rainy, 24-28°C",
      visibility: "Moderate",
      activities: ["Mangrove tours", "Indoor exhibits", "Research activities"],
      recommendation: "Limited trail access due to flooding - focus on covered areas and educational programs"
    },
    {
      id: 3,
      name: "Peak Wildlife",
      period: "December - February",
      avgVisitors: 523,
      satisfaction: 97,
      weather: "Cool, 22-26°C",
      visibility: "Excellent",
      activities: ["Migratory bird watching", "Nocturnal tours", "Wildlife photography", "Research expeditions"],
      recommendation: "Best period for biodiversity observation - advance booking required due to high demand"
    },
    {
      id: 4,
      name: "Nesting Period",
      period: "March - May",
      avgVisitors: 398,
      satisfaction: 89,
      weather: "Warm, 26-30°C",
      visibility: "Good",
      activities: ["Nesting site observation", "Conservation education", "Guided eco-tours"],
      recommendation: "Restricted access to certain zones to protect nesting species - educational programs emphasized"
    }
  ];

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      {/* Navigation Header */}
      <NavigationHeader />
      
      {/* Add padding to account for fixed header */}
      <div className="pt-20">
        {/* Filters Section */}
        <div className="bg-card rounded-lg p-6 shadow-conservation border border-border mb-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-1">Analytics Overview</h2>
              <p className="text-sm text-muted-foreground">Real-time visitor metrics and conservation impact tracking</p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
              <Select
                options={zoneOptions}
                value={selectedZone}
                onChange={setSelectedZone}
                placeholder="Select zone"
                className="w-full sm:w-48"
              />
              <Select
                options={timeRangeOptions}
                value={timeRange}
                onChange={setTimeRange}
                placeholder="Select time range"
                className="w-full sm:w-48"
              />
            </div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {metricsData?.map((metric, index) => (
            <MetricCard key={index} {...metric} />
          ))}
        </div>

        {/* Visitor Flow Heatmap */}
        <div className="mb-8">
          <VisitorFlowHeatmap data={heatmapData} />
        </div>

        {/* Demographic Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <DemographicChart 
            data={originData} 
            title="Visitor Origins" 
            subtitle="Geographic distribution analysis"
            type="origin"
          />
          <DemographicChart 
            data={ageData} 
            title="Age Demographics" 
            subtitle="Visitor age group breakdown"
            type="age"
          />
        </div>

        <div className="mb-8">
          <DemographicChart 
            data={durationData} 
            title="Visit Duration Patterns" 
            subtitle="Time spent in park analysis"
            type="duration"
          />
        </div>

        {/* Conversion Tracking */}
        <div className="mb-8">
          <ConversionTracker data={conversionData} />
        </div>

        {/* Environmental Impact */}
        <div className="mb-8">
          <EnvironmentalImpactChart data={environmentalData} />
        </div>

        {/* Predictive Analytics */}
        <div className="mb-8">
          <PredictiveAnalytics data={predictiveData} recommendations={recommendations} />
        </div>

        {/* Seasonal Trends */}
        <div className="mb-8">
          <SeasonalTrends data={seasonalData} />
        </div>

        {/* Quick Actions */}
        <div className="bg-card rounded-lg p-6 shadow-conservation border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/visitor-planning-hub">
              <Button variant="outline" fullWidth iconName="Calendar" iconPosition="left">
                Visitor Planning
              </Button>
            </Link>
            <Link to="/research-data-portal">
              <Button variant="outline" fullWidth iconName="Database" iconPosition="left">
                Research Portal
              </Button>
            </Link>
            <Link to="/emergency-alert-system">
              <Button variant="outline" fullWidth iconName="AlertTriangle" iconPosition="left">
                Emergency Alerts
              </Button>
            </Link>
            <Link to="/community-engagement-platform">
              <Button variant="outline" fullWidth iconName="Users" iconPosition="left">
                Community Hub
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitorAnalyticsDashboard;